import React from 'react';
import { connect, ConnectedProps } from '@cerebral/react';

import quiz from 'toolData/quiz';

import SecondaryPage from 'challenge/common/KnowledgeComponents/SecondaryPage/Desktop';
import AnswerComponent from '../AnswerComponent';
import {
  InnerWrapper,
  Title,
  SmallText,
  ExtraTextWrapper,
  ExtraText,
  Button,
} from './elements';
import { useText } from 'common/hooks';
import { moduleSequences as sequences, state } from 'app/pwa/app.cerebral';

const deps = {
  tag: state.challenge.tagModal.key,
  answers: state.challenge.userChallenge.quiz,
  quizPageIndex: state.challenge.tagModal.quizPageIndex,
  answerIsSaving: state.challenge.tagModal.answerIsSaving,
  quizAnswerSelected: sequences.challenge.tagModal.quizAnswerSelected,
  quizContinueClicked: sequences.challenge.tagModal.quizContinueClicked,
  toggleQuiz: sequences.challenge.tagModal.toggleQuiz,
};

type Props = {
  readonly className?: string;
};

function QuizPage({
  className,
  tag,
  answers,
  answerIsSaving,
  quizPageIndex,
  quizAnswerSelected,
  quizContinueClicked,
  toggleQuiz,
}: Props & typeof deps & ConnectedProps): React.ReactElement {
  const t = useText();

  const question = quiz[tag][quizPageIndex - 1];
  const answer = answers?.[question.question]?.answer;

  return (
    <SecondaryPage
      className={className}
      backOnClick={() => toggleQuiz()}
      buttonText={t(`challenge.quiz.back.${tag}`)}
    >
      <InnerWrapper>
        <SmallText>
          {t(`challenge.quiz.common.num_questions`, {
            currentQuestion: quizPageIndex,
            totalQuestions: quiz[tag]?.length,
          })}
        </SmallText>
        <Title>{t(`challenge.quiz.${question.question}.question`)}</Title>
        {question.options.map((option, index) => (
          <AnswerComponent
            key={option.option}
            number={index + 1}
            onClick={() => {
              if (answer) return;

              quizAnswerSelected({
                category: tag,
                question: question.question,
                answer: option.option,
              });
            }}
            isLoading={answerIsSaving}
            hasAnswered={Boolean(answer)}
            answerText={t(`challenge.quiz.${option.text}`)}
            currentAnswer={answer === option.option}
            wasCorrect={answer === question.correctOption}
            isHighlighted={answer && option.option === question.correctOption}
          />
        ))}
        {answer && (
          <>
            <ExtraTextWrapper>
              <ExtraText>
                {t(`challenge.quiz.${question.question}.explanation`)}
              </ExtraText>
            </ExtraTextWrapper>
            <Button onClick={() => quizContinueClicked()}>
              {t(`challenge.quiz.common.continue`)}
            </Button>
          </>
        )}
      </InnerWrapper>
    </SecondaryPage>
  );
}

export default connect<Props, typeof deps>(deps, QuizPage);
